import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Payments  from './pages/Payments';
import Settings  from './pages/Settings';
import Login     from './pages/Login';
import MemberPortal from './pages/MemberPortal';
import Landing from './pages/Landing';
import Members from './pages/Members';
import Accounts from './pages/Accounts';
import Transactions from './pages/Transactions';
import Reconciliation from './pages/Reconciliation';
import Merchants from './pages/Merchants';
import Tenants from './pages/Tenants';
import Plans from './pages/Plans';
import { defaultRouteForRole, isBackofficeRole } from './utils/navigation';
import { getCurrentUser, hydrateUserFromAccessToken, startSessionRefreshLoop, stopSessionRefreshLoop } from './utils/auth';

function isMemberRole(role) {
  return role === 'member';
}

function PrivateRoute({ children, allowMember = true, allowedRoles = null }) {
  const token = localStorage.getItem('access_token');
  if (!token) {
    const next = `${window.location.pathname}${window.location.search || ''}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  const user = getCurrentUser();
  if (!allowMember && user && isMemberRole(user.role)) {
    return <Navigate to="/member" replace />;
  }

  if (!user?.role) {
    return <Navigate to="/login" replace />;
  }

  if (!allowMember && !isBackofficeRole(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (Array.isArray(allowedRoles) && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function MemberOnlyRoute({ children }) {
  const token = localStorage.getItem('access_token');
  const user = getCurrentUser();

  if (!token) {
    const next = `${window.location.pathname}${window.location.search || ''}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isMemberRole(user.role)) return <Navigate to={defaultRouteForRole(user.role)} replace />;

  return children;
}

function HomeRoute() {
  const token = localStorage.getItem('access_token');
  if (!token) return <Landing />;

  const user = getCurrentUser();
  return <Navigate to={defaultRouteForRole(user?.role)} replace />;
}

function App() {
  useEffect(() => {
    hydrateUserFromAccessToken();
    const stopLoop = startSessionRefreshLoop();

    return () => {
      stopLoop?.();
      stopSessionRefreshLoop();
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<PrivateRoute allowMember={false}><Dashboard /></PrivateRoute>} />
        <Route path="/payments" element={<PrivateRoute allowMember={false}><Payments /></PrivateRoute>} />
        <Route path="/members" element={<PrivateRoute allowMember={false} allowedRoles={['superadmin', 'admin', 'manager', 'operator', 'auditor', 'compliance_officer']}><Members /></PrivateRoute>} />
        <Route path="/accounts" element={<PrivateRoute allowMember={false} allowedRoles={['superadmin', 'admin', 'manager', 'operator', 'auditor', 'compliance_officer']}><Accounts /></PrivateRoute>} />
        <Route path="/transactions" element={<PrivateRoute allowMember={false} allowedRoles={['superadmin', 'admin', 'manager', 'operator', 'auditor', 'compliance_officer']}><Transactions /></PrivateRoute>} />
        <Route path="/reconciliation" element={<PrivateRoute allowMember={false} allowedRoles={['superadmin', 'admin', 'auditor', 'compliance_officer']}><Reconciliation /></PrivateRoute>} />
        <Route path="/merchants" element={<PrivateRoute allowMember={false} allowedRoles={['superadmin', 'admin', 'auditor', 'compliance_officer']}><Merchants /></PrivateRoute>} />
        <Route path="/tenants" element={<PrivateRoute allowMember={false} allowedRoles={['superadmin']}><Tenants /></PrivateRoute>} />
        <Route path="/plans" element={<PrivateRoute allowMember={false} allowedRoles={['superadmin']}><Plans /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute allowMember={false} allowedRoles={['admin', 'superadmin']}><Settings /></PrivateRoute>} />
        <Route path="/member" element={<MemberOnlyRoute><MemberPortal /></MemberOnlyRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
